import jsPDF from 'jspdf';

/**
 * Genera PDF profesional del resumen de consulta médica.
 * Cubre tanto consulta de primera vez como nota de evolución (subsecuente).
 *
 * Args:
 *   paciente — datos del paciente
 *   consulta — formData completo (incluye evolucion_problemas si subsecuente)
 *   diagnosticos — array de DiagnosticoConsulta
 *   medicamentos — array de medicamentos de la receta
 *   ordenes — opcional, array de OrdenConsulta
 *   archivosAdjuntos — opcional, array de archivos del paciente
 *   doctor — opcional, override de los datos del médico
 *   imprimir — boolean, si true abre diálogo de impresión, si false descarga
 */
export async function exportConsultaPDF({
  paciente, consulta, diagnosticos = [], medicamentos = [],
  ordenes = [], archivosAdjuntos = [],
  doctor, imprimir = false,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const m = 36;          // margen lateral más generoso
  let y = m;

  // Datos del médico (con fallback al estándar)
  const dr = {
    nombre: doctor?.nombre || 'Dr. Raul Olier',
    rfc: doctor?.rfc || 'OIPH650912881',
    especialidad1: doctor?.especialidad1 || 'Medicina General',
    cedula: doctor?.cedula || '3625181',
    universidad: doctor?.universidad || 'Universidad de Cartagena - Colombia',
    especialidad2: doctor?.especialidad2 || 'Maestría en Cirugía Estética',
    dgp: doctor?.dgp || '6698688',
  };

  // Helpers
  const fmtFecha = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' });
  };
  const fmtFechaCorta = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const calcEdad = (iso) => {
    if (!iso) return '—';
    return Math.floor((Date.now() - new Date(iso).getTime()) / (365.25 * 24 * 3600 * 1000));
  };
  const sexoTxt = (s) => s === 'femenino' ? 'Femenino' : s === 'masculino' ? 'Masculino' : (s || '—');

  // Cargar logo y firma como base64
  let logoImg = null;
  let firmaImg = null;
  try {
    const toDataUrl = (url) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
    logoImg = await toDataUrl('/logo-nara-png.png');
    firmaImg = await toDataUrl('/firma_doctor.png');
  } catch (e) { /* sin imágenes */ }

  // Helper de avance de página
  const checkPageBreak = (need = 60) => {
    if (y + need > ph - 80) {  // 80px reservado para footer
      doc.addPage();
      y = m;
    }
  };

  // Helper para sección con header colorido
  const sectionHeader = (titulo, color = [30, 58, 95]) => {
    checkPageBreak(28);
    doc.setFillColor(color[0], color[1], color[2]);
    doc.rect(m, y, pw - 2 * m, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.text(titulo.toUpperCase(), m + 6, y + 11);
    y += 22;
    doc.setTextColor(30, 41, 59);
  };

  const fieldLabel = (label, valor, opts = {}) => {
    checkPageBreak(14);
    const labelW = opts.labelW || 95;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(112, 112, 112);
    doc.text(`${label}:`, m, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 41, 59);
    const lines = doc.splitTextToSize(String(valor || '—'), pw - 2 * m - labelW);
    doc.text(lines, m + labelW, y);
    y += lines.length * 11 + 3;
  };

  const paragraph = (texto, opts = {}) => {
    if (!texto) return;
    const sz = opts.size || 9.5;
    doc.setFontSize(sz);
    doc.setFont('helvetica', opts.italic ? 'italic' : 'normal');
    doc.setTextColor(30, 41, 59);
    const lines = doc.splitTextToSize(String(texto), pw - 2 * m);
    for (const line of lines) {
      checkPageBreak(12);
      doc.text(line, m, y);
      y += sz * 1.25;
    }
    y += 4;
  };

  // ===========================================================================
  // 1) ENCABEZADO con logo + título + datos médico
  // ===========================================================================
  if (logoImg) doc.addImage(logoImg, 'PNG', m - 6, m - 6, 60, 60);

  const headerX = logoImg ? m + 70 : m;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 58, 95);
  const tipoLabel = consulta.tipo_consulta === 'subsecuente'
    ? 'NOTA DE EVOLUCIÓN'
    : consulta.tipo_consulta === 'urgencia'
      ? 'CONSULTA DE URGENCIA'
      : consulta.tipo_consulta === 'interconsulta'
        ? 'INTERCONSULTA'
        : 'CONSULTA MÉDICA';
  doc.text(tipoLabel, headerX, m + 12);

  // Datos del médico bajo el título
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 58, 95);
  doc.text(dr.nombre, headerX, m + 26);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(8);
  doc.text(`${dr.especialidad1} · Céd. Prof. ${dr.cedula}`, headerX, m + 36);
  doc.text(`${dr.universidad}`, headerX, m + 45);
  if (dr.especialidad2) doc.text(`${dr.especialidad2} · DGP ${dr.dgp}`, headerX, m + 54);

  // Fecha en la esquina derecha
  doc.setFontSize(8);
  doc.setTextColor(112, 112, 112);
  doc.setFont('helvetica', 'bold');
  doc.text('FECHA', pw - m, m + 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(10);
  doc.text(fmtFecha(consulta.fecha_consulta || new Date().toISOString()), pw - m, m + 26, { align: 'right' });

  y = m + 72;
  // Línea divisoria
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.5);
  doc.line(m, y, pw - m, y);
  y += 14;

  // ===========================================================================
  // 2) DATOS DEL PACIENTE
  // ===========================================================================
  sectionHeader('Datos del paciente');
  fieldLabel('Nombre', (paciente?.nombre_completo || '—').toUpperCase());
  const edadSexo = `${calcEdad(paciente?.fecha_nacimiento)} años · ${sexoTxt(paciente?.sexo)}`;
  fieldLabel('Edad / Sexo', edadSexo);
  if (paciente?.fecha_nacimiento) fieldLabel('Fecha nacimiento', fmtFechaCorta(paciente.fecha_nacimiento));
  if (paciente?.telefono) fieldLabel('Teléfono', paciente.telefono);
  if (paciente?.tipo_sangre) fieldLabel('Tipo de sangre', paciente.tipo_sangre);
  y += 4;

  // ===========================================================================
  // 3) NOTA DE EVOLUCIÓN — solo cuando es subsecuente
  // ===========================================================================
  const evol = consulta.evolucion_problemas || {};
  const esEvolucion = consulta.tipo_consulta === 'subsecuente' && (
    evol.evolucion_general || evol.adherencia_tratamiento ||
    (Array.isArray(evol.problemas) && evol.problemas.length > 0) ||
    evol.conciliacion_medicamentos
  );

  if (esEvolucion) {
    if (evol.evolucion_general) {
      sectionHeader('Evolución desde la última consulta', [99, 102, 241]);
      paragraph(evol.evolucion_general);
    }

    if (evol.adherencia_tratamiento) {
      checkPageBreak(20);
      const labelAdh = {
        total: 'Adherencia total',
        parcial: 'Adherencia parcial',
        no: 'Sin adherencia',
      }[evol.adherencia_tratamiento] || evol.adherencia_tratamiento;
      const colorAdh = {
        total: [16, 185, 129],
        parcial: [245, 158, 11],
        no: [239, 68, 68],
      }[evol.adherencia_tratamiento] || [100, 116, 139];
      doc.setFillColor(...colorAdh);
      doc.roundedRect(m, y, 120, 14, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(`ADHERENCIA: ${labelAdh.toUpperCase()}`, m + 6, y + 10);
      y += 18;
      if (evol.adherencia_motivo) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(80, 80, 80);
        const lines = doc.splitTextToSize(`Motivo: ${evol.adherencia_motivo}`, pw - 2 * m);
        doc.text(lines, m, y);
        y += lines.length * 11 + 3;
      }
      y += 4;
    }

    // Evolución de problemas
    if (Array.isArray(evol.problemas) && evol.problemas.length > 0) {
      sectionHeader('Evolución de problemas activos', [99, 102, 241]);
      const estadoColor = (e) => ({
        mejor: [16, 185, 129],
        igual: [100, 116, 139],
        peor: [239, 68, 68],
        resuelto: [14, 165, 233],
        nuevo: [168, 85, 247],
      }[e] || [100, 116, 139]);
      const estadoLabel = (e) => ({
        mejor: 'Mejor',
        igual: 'Igual',
        peor: 'Peor',
        resuelto: 'Resuelto',
        nuevo: 'Nuevo',
      }[e] || e || '—');
      for (const p of evol.problemas) {
        checkPageBreak(22);
        const c = estadoColor(p.estado);
        // Chip estado
        doc.setFillColor(...c);
        doc.roundedRect(m, y, 50, 12, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.text(estadoLabel(p.estado).toUpperCase(), m + 4, y + 8);
        // Texto del dx
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.text(p.codigo_cie10 || '', m + 56, y + 8);
        doc.setFont('helvetica', 'normal');
        const txt = ` · ${p.descripcion || ''}`;
        const codW = doc.getTextWidth(p.codigo_cie10 || '');
        doc.text(txt, m + 56 + codW, y + 8);
        y += 14;
        if (p.comentario) {
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(80, 80, 80);
          const lines = doc.splitTextToSize(p.comentario, pw - 2 * m - 56);
          doc.text(lines, m + 56, y + 4);
          y += lines.length * 10 + 2;
        }
        y += 3;
      }
      y += 2;
    }
  }

  // ===========================================================================
  // 4) MOTIVO + PADECIMIENTO ACTUAL (siempre — pero título cambia)
  // ===========================================================================
  if (consulta.motivo_consulta) {
    sectionHeader('Motivo de consulta');
    paragraph(consulta.motivo_consulta, { size: 10 });
  }

  if (consulta.padecimiento_actual && !esEvolucion) {
    sectionHeader('Padecimiento actual');
    paragraph(consulta.padecimiento_actual);
  }

  // ===========================================================================
  // 5) INTERROGATORIO POR APARATOS Y SISTEMAS
  // ===========================================================================
  const ia = consulta.interrogatorio_aparatos_sistemas;
  const iaTexto = typeof ia === 'string' ? ia : (ia && typeof ia === 'object' && ia.texto ? ia.texto : '');
  if (iaTexto) {
    sectionHeader('Interrogatorio por aparatos y sistemas');
    paragraph(iaTexto);
  }

  // ===========================================================================
  // 6) SIGNOS VITALES (estilo grilla)
  // ===========================================================================
  const sv = consulta.signos_vitales || {};
  const svItems = [];
  if (sv.tension_arterial) svItems.push(['TA', `${sv.tension_arterial} mmHg`]);
  else if (sv.presion_arterial_sistolica && sv.presion_arterial_diastolica) {
    svItems.push(['TA', `${sv.presion_arterial_sistolica}/${sv.presion_arterial_diastolica} mmHg`]);
  }
  if (sv.frecuencia_cardiaca) svItems.push(['FC', `${sv.frecuencia_cardiaca} lpm`]);
  if (sv.frecuencia_respiratoria) svItems.push(['FR', `${sv.frecuencia_respiratoria} rpm`]);
  if (sv.temperatura) svItems.push(['T°', `${sv.temperatura} °C`]);
  if (sv.saturacion_oxigeno) svItems.push(['SpO₂', `${sv.saturacion_oxigeno} %`]);
  if (sv.peso) svItems.push(['Peso', `${sv.peso} kg`]);
  if (sv.talla) svItems.push(['Talla', `${sv.talla} cm`]);
  if (sv.imc) svItems.push(['IMC', sv.imc]);
  if (sv.glucemia) svItems.push(['Glucemia', `${sv.glucemia} mg/dL`]);

  if (svItems.length > 0) {
    sectionHeader('Signos vitales');
    const cols = 4;
    const colW = (pw - 2 * m) / cols;
    let col = 0;
    let rowY = y;
    for (const [label, valor] of svItems) {
      const x = m + col * colW;
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(112, 112, 112);
      doc.text(label.toUpperCase(), x, rowY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      doc.text(valor, x, rowY + 12);
      col++;
      if (col === cols) {
        col = 0;
        rowY += 26;
        checkPageBreak(26);
      }
    }
    if (col !== 0) rowY += 26;
    y = rowY + 4;
  }

  // ===========================================================================
  // 7) EXPLORACIÓN FÍSICA
  // ===========================================================================
  const ef = consulta.exploracion_fisica || {};
  const efSecciones = [
    ['piel_faneras', 'Piel y faneras', [249, 115, 22]],
    ['cabeza_cuello', 'Cabeza y cuello', [236, 72, 153]],
    ['torax', 'Tórax', [14, 165, 233]],
    ['abdomen', 'Abdomen', [245, 158, 11]],
    ['genitales', 'Genitales', [20, 184, 166]],
    ['extremidades', 'Extremidades', [16, 185, 129]],
    ['neurologico', 'Neurológico', [139, 92, 246]],
    ['observaciones', 'Observaciones', [100, 116, 139]],
  ];
  const efPresentes = efSecciones.filter(([k]) => ef[k] && String(ef[k]).trim());
  if (efPresentes.length > 0) {
    sectionHeader('Exploración física');
    for (const [k, label, color] of efPresentes) {
      checkPageBreak(20);
      // Tag de color
      doc.setFillColor(...color);
      doc.rect(m, y, 3, 12, 'F');
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...color);
      doc.text(label.toUpperCase(), m + 8, y + 9);
      y += 14;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(30, 41, 59);
      const lines = doc.splitTextToSize(String(ef[k]), pw - 2 * m - 8);
      for (const line of lines) {
        checkPageBreak(11);
        doc.text(line, m + 8, y);
        y += 11;
      }
      y += 4;
    }
  }

  // ===========================================================================
  // 8) RESULTADOS DE ESTUDIOS REVISADOS (nota de evolución)
  // ===========================================================================
  const estudiosRev = Array.isArray(evol.estudios_revisados) ? evol.estudios_revisados : [];
  if (estudiosRev.length > 0) {
    sectionHeader('Estudios revisados');
    for (const est of estudiosRev) {
      checkPageBreak(30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(8, 145, 178);
      doc.text(`🧪 ${est.nombre || 'Estudio'}${est.fecha ? ` (${est.fecha})` : ''}`, m, y);
      y += 12;
      const params = est.parametros || [];
      const anormales = params.filter(p => p.estado === 'alto' || p.estado === 'bajo').length;
      if (anormales > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(220, 38, 38);
        doc.text(`${anormales} parámetro${anormales > 1 ? 's' : ''} fuera de rango`, m, y);
        y += 10;
      }
      // Lista en 2 columnas
      const colsP = 2;
      const colWP = (pw - 2 * m) / colsP;
      let colP = 0;
      let rowYp = y;
      for (const p of params) {
        const x = m + colP * colWP;
        const color = p.estado === 'alto' ? [220, 38, 38] : p.estado === 'bajo' ? [29, 78, 216] : [21, 128, 61];
        const flecha = p.estado === 'alto' ? '↑' : p.estado === 'bajo' ? '↓' : '';
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text(`${p.nombre || ''}:`, x, rowYp);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...color);
        doc.text(`${p.valor || ''}${p.unidad ? ' ' + p.unidad : ''} ${flecha}`, x + 80, rowYp);
        colP++;
        if (colP === colsP) { colP = 0; rowYp += 11; checkPageBreak(11); }
      }
      if (colP !== 0) rowYp += 11;
      y = rowYp + 2;
      if (est.interpretacion_breve) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(80, 80, 80);
        const lines = doc.splitTextToSize(est.interpretacion_breve, pw - 2 * m);
        doc.text(lines, m, y);
        y += lines.length * 10 + 4;
      }
    }
  }

  // ===========================================================================
  // 9) IMPRESIÓN DIAGNÓSTICA
  // ===========================================================================
  if (consulta.impresion_diagnostica) {
    sectionHeader('Impresión diagnóstica');
    paragraph(consulta.impresion_diagnostica, { size: 10 });
  }

  if (diagnosticos.length > 0) {
    sectionHeader('Diagnósticos CIE-10', [16, 185, 129]);
    for (const d of diagnosticos) {
      checkPageBreak(18);
      const esPrincipal = d.tipo_diagnostico === 'principal';
      // Chip
      const chipColor = esPrincipal ? [16, 185, 129] : [148, 163, 184];
      doc.setFillColor(...chipColor);
      doc.roundedRect(m, y, esPrincipal ? 60 : 70, 12, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.text(esPrincipal ? 'PRINCIPAL' : 'SECUNDARIO', m + 4, y + 8);
      // Código + descripción
      doc.setTextColor(30, 41, 59);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      const offsetX = esPrincipal ? 66 : 76;
      doc.text(d.codigo_cie10 || '', m + offsetX, y + 9);
      doc.setFont('helvetica', 'normal');
      const codW = doc.getTextWidth(d.codigo_cie10 || '');
      doc.text(` · ${d.descripcion_cie10 || ''}`, m + offsetX + codW, y + 9);
      y += 16;
      // Certeza + temporalidad
      const meta = [];
      if (d.certeza) meta.push(d.certeza === 'definitivo' ? 'Definitivo' : 'Presuntivo');
      if (d.temporalidad) meta.push(d.temporalidad === 'subsecuente' ? 'Subsecuente' : 'Primera vez');
      if (meta.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(112, 112, 112);
        doc.setFont('helvetica', 'italic');
        doc.text(meta.join(' · '), m + offsetX, y);
        y += 10;
      }
      y += 2;
    }
  }

  // ===========================================================================
  // 10) PRONÓSTICO
  // ===========================================================================
  if (consulta.pronostico) {
    sectionHeader('Pronóstico', [139, 92, 246]);
    const p = consulta.pronostico.toLowerCase();
    const colP = p === 'bueno' ? [16, 185, 129] : p === 'reservado' ? [245, 158, 11] : [239, 68, 68];
    doc.setFillColor(...colP);
    doc.roundedRect(m, y, 90, 16, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(consulta.pronostico.toUpperCase(), m + 8, y + 11);
    y += 22;
  }

  // ===========================================================================
  // 11) ESTUDIOS / INTERCONSULTAS / ÓRDENES
  // ===========================================================================
  if (consulta.estudios_solicitados) {
    sectionHeader('Estudios solicitados');
    paragraph(consulta.estudios_solicitados);
  }
  if (consulta.interconsultas_solicitadas) {
    sectionHeader('Interconsultas solicitadas');
    paragraph(consulta.interconsultas_solicitadas);
  }

  // Órdenes generadas (si las hay)
  if (Array.isArray(ordenes) && ordenes.length > 0) {
    sectionHeader('Documentos generados', [6, 182, 212]);
    const tipoLabels = {
      laboratorio: '🧪 Orden de laboratorio',
      interconsulta: '🏥 Interconsulta',
      hospitalizacion: '🛏 Hospitalización',
      certificado: '📋 Certificado médico',
      justificante: '📅 Justificante médico',
      libre: '✏ Orden libre',
    };
    for (const o of ordenes) {
      checkPageBreak(20);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(8, 145, 178);
      doc.text(tipoLabels[o.tipo] || o.tipo, m, y);
      y += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      let resumen = '';
      if (o.tipo === 'laboratorio' && o.contenido?.estudios) {
        resumen = o.contenido.estudios.map(e => e.nombre).join(' · ');
      } else if (o.tipo === 'interconsulta') {
        resumen = `${o.contenido?.especialidad || ''} — ${o.contenido?.motivo || ''}`;
      } else {
        resumen = o.contenido?.diagnostico || o.contenido?.texto || o.contenido?.indicaciones_hospitalarias || '';
      }
      if (resumen) {
        const lines = doc.splitTextToSize(resumen, pw - 2 * m);
        doc.text(lines, m + 4, y);
        y += lines.length * 11 + 3;
      }
    }
  }

  // ===========================================================================
  // 12) PLAN DE TRATAMIENTO
  // ===========================================================================
  if (consulta.plan_tratamiento) {
    sectionHeader('Plan de tratamiento', [245, 158, 11]);
    paragraph(consulta.plan_tratamiento);
  }

  // ===========================================================================
  // 13) RECETA (medicamentos)
  // ===========================================================================
  if (medicamentos.length > 0) {
    sectionHeader(`Receta médica (${medicamentos.length} medicamento${medicamentos.length > 1 ? 's' : ''})`, [139, 92, 246]);
    for (let i = 0; i < medicamentos.length; i++) {
      const med = medicamentos[i];
      checkPageBreak(35);
      // Número circular
      doc.setFillColor(245, 243, 255);
      doc.circle(m + 8, y + 6, 8, 'F');
      doc.setTextColor(124, 58, 237);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(String(i + 1), m + 6, y + 9);
      // Línea 1: nombre + presentación + dosis
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      const linea1 = [med.nombre, med.presentacion, med.dosis].filter(Boolean).join(' · ');
      doc.text(linea1, m + 22, y + 8);
      y += 13;
      // Línea 2: vía + frecuencia + duración
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(80, 80, 80);
      const linea2 = [
        med.via && `Vía ${med.via}`,
        med.frecuencia,
        med.duracion && `por ${med.duracion}`,
      ].filter(Boolean).join('  ·  ');
      if (linea2) {
        doc.text(linea2, m + 22, y);
        y += 11;
      }
      // Línea 3: indicaciones específicas
      if (med.indicaciones) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(124, 58, 237);
        const lines = doc.splitTextToSize(med.indicaciones, pw - 2 * m - 22);
        doc.text(lines, m + 22, y);
        y += lines.length * 11;
      }
      y += 4;
    }
  }

  // ===========================================================================
  // 14) INDICACIONES AL PACIENTE
  // ===========================================================================
  if (consulta.indicaciones_paciente) {
    sectionHeader('Indicaciones al paciente', [6, 182, 212]);
    paragraph(consulta.indicaciones_paciente, { size: 10 });
  }

  // ===========================================================================
  // 15) ARCHIVOS ADJUNTOS DEL PACIENTE (referencias)
  // ===========================================================================
  if (Array.isArray(archivosAdjuntos) && archivosAdjuntos.length > 0) {
    sectionHeader('Archivos adjuntos del paciente', [6, 182, 212]);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(112, 112, 112);
    doc.text('Documentos digitales archivados en el expediente:', m, y);
    y += 11;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);
    for (const a of archivosAdjuntos) {
      checkPageBreak(12);
      const tipo = a.archivo_tipo === 'application/pdf' ? '📄' : '🖼️';
      doc.text(`${tipo}  ${a.archivo_nombre || 'Archivo'}`, m, y);
      y += 11;
    }
    y += 4;
  }

  // ===========================================================================
  // FOOTER en cada página: paginación + firma del médico (última página)
  // ===========================================================================
  const numPaginas = doc.internal.getNumberOfPages();
  for (let i = 1; i <= numPaginas; i++) {
    doc.setPage(i);
    // Línea superior del footer
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(m, ph - 60, pw - m, ph - 60);

    // Página
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Página ${i} de ${numPaginas}  ·  NARA Care  ·  Documento clínico confidencial — NOM-024-SSA3`,
      pw / 2, ph - 14, { align: 'center' }
    );

    // Firma solo en la última página
    if (i === numPaginas) {
      const fY = ph - 56;
      // Firma del doctor (imagen)
      if (firmaImg) {
        try { doc.addImage(firmaImg, 'PNG', pw - m - 130, fY - 20, 110, 35); } catch (e) {}
      }
      // Línea de firma
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.4);
      doc.line(pw - m - 140, fY + 18, pw - m, fY + 18);
      // Datos del médico debajo de la línea
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(dr.nombre, pw - m - 70, fY + 28, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(112, 112, 112);
      doc.text(`${dr.especialidad1}  ·  Céd. Prof. ${dr.cedula}`, pw - m - 70, fY + 36, { align: 'center' });
    }
  }

  // ===========================================================================
  // OUTPUT
  // ===========================================================================
  const nombrePac = (paciente?.nombre_completo || 'paciente').replace(/\s+/g, '_');
  const fechaArchivo = new Date().toISOString().slice(0, 10);
  const tipoArchivo = consulta.tipo_consulta === 'subsecuente' ? 'nota_evolucion' : 'consulta';
  const filename = `${tipoArchivo}_${nombrePac}_${fechaArchivo}.pdf`;

  if (imprimir) {
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  } else {
    doc.save(filename);
  }
}
